import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Id } from "@stoneflow/backend/convex/_generated/dataModel"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { AlertTriangleIcon, GripVerticalIcon, PlusIcon, TrashIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import { FieldLibraryPicker } from "#/components/field-library-picker"
import { RouteError } from "#/components/route-error"
import { SettingsSkeleton } from "#/components/skeletons/settings-skeleton"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "#/components/ui/alert-dialog"
import { Badge } from "#/components/ui/badge"
import { Button } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { Input } from "#/components/ui/input"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "#/components/ui/select"
import { Spinner } from "#/components/ui/spinner"
import { Switch } from "#/components/ui/switch"
import { useRoles } from "#/hooks/use-roles"
import { trackEvent } from "#/lib/posthog"

export const Route = createFileRoute("/_authenticated/admin/tenants/$orgId/fields")({
	errorComponent: RouteError,
	component: FieldSchemasTab,
})

const BASE_FIELD_IDS = new Set(["description", "priority"])

interface FieldDef {
	id: string
	libraryKey?: string
	label: string
	type: string
	group?: string
	required?: boolean
	order?: number
	width?: "full" | "half" | "third"
	placeholder?: string
	helpText?: string
	options?: { label: string; value: string | number }[]
	autocompleteSource?: string
	validation?: Record<string, unknown>
	[key: string]: unknown
}

function FieldSchemasTab() {
	const { orgId } = Route.useParams()
	const logTypes = useQuery(api.stonelog.logTypes.list, { actingOrgId: orgId })
	const [selectedLogTypeId, setSelectedLogTypeId] = useState<string>("")

	if (!logTypes) {
		return <SettingsSkeleton />
	}

	const selected = logTypes.find((lt) => lt._id === selectedLogTypeId)

	return (
		<section className="space-y-6">
			<div>
				<h3>Field schemas</h3>
				<p className="text-sm text-foreground-muted">
					Customise the fields captured on each log type for this tenant — rename labels, add fields
					from the library, hide fields, or reorder them. Hiding a field keeps existing data in the
					database; it just stops collecting and displaying it.
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Select log type</CardTitle>
				</CardHeader>
				<CardContent>
					{logTypes.length === 0 ? (
						<p className="text-sm text-foreground-muted">
							This tenant has no log types installed yet.
						</p>
					) : (
						<Select value={selectedLogTypeId} onValueChange={setSelectedLogTypeId}>
							<SelectTrigger className="max-w-xs">
								<SelectValue placeholder="Choose a log type..." aria-label="Log type">
									{selected?.name}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								{logTypes.map((lt) => (
									<SelectItem key={lt._id} value={lt._id}>
										{lt.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				</CardContent>
			</Card>

			{selectedLogTypeId && selected && (
				<SchemaEditor
					logTypeId={selectedLogTypeId as Id<"logTypes">}
					logTypeSlug={selected.slug}
					actingOrgId={orgId}
				/>
			)}
		</section>
	)
}

function fieldsEqual(a: FieldDef[], b: FieldDef[]): boolean {
	return JSON.stringify(a) === JSON.stringify(b)
}

function SchemaEditor({
	logTypeId,
	logTypeSlug,
	actingOrgId,
}: {
	logTypeId: Id<"logTypes">
	logTypeSlug: string
	actingOrgId?: string
}) {
	const { isPlatformAdmin } = useRoles()
	const current = useQuery(api.stonelog.fieldSchemas.getCurrent, { logTypeId, actingOrgId })
	const versions = useQuery(api.stonelog.fieldSchemas.listVersions, { logTypeId, actingOrgId })
	const library = useQuery(api.platform.fieldLibrary.list, {
		logTypeSlug,
		includeArchived: true,
	})
	const resetSchema = useMutation(api.stonelog.fieldSchemas.reset)
	const rollbackSchema = useMutation(api.stonelog.fieldSchemas.rollback)
	const saveSchema = useMutation(api.stonelog.fieldSchemas.save)
	const addLibraryFields = useMutation(api.stonelog.fieldSchemas.addLibraryFields)
	const seedLibrary = useMutation(api.platform.seedFieldLibrary.seedDefaults)

	const [pickerOpen, setPickerOpen] = useState(false)
	const [draft, setDraft] = useState<FieldDef[] | null>(null)
	const [saving, setSaving] = useState(false)
	const [seeding, setSeeding] = useState(false)

	if (!current || library === undefined) {
		return (
			<div className="flex items-center justify-center p-8">
				<Spinner />
			</div>
		)
	}

	const currentVersion = current.version
	const persisted = current.fields as FieldDef[]
	const fields = draft ?? persisted
	const dirty = draft !== null && !fieldsEqual(draft, persisted)
	const usedLibraryKeys = fields.map((f) => f.libraryKey).filter((k): k is string => Boolean(k))
	const libraryEmpty = library.length === 0

	function patchField(index: number, patch: Partial<FieldDef>) {
		const next = (draft ?? persisted).map((f, i) => (i === index ? { ...f, ...patch } : f))
		setDraft(next)
	}

	function removeField(index: number) {
		const next = (draft ?? persisted).filter((_, i) => i !== index)
		setDraft(next)
	}

	function moveField(from: number, to: number) {
		if (from === to) return
		const next = arrayMove(draft ?? persisted, from, to)
		setDraft(next)
	}

	async function handleSave() {
		if (!draft) return
		setSaving(true)
		try {
			const withOrder = draft.map((f, i) => ({ ...f, order: i + 1 }))
			const saved = await saveSchema({
				logTypeId,
				fields: withOrder,
				expectedCurrentVersion: currentVersion,
				actingOrgId,
			})
			trackEvent("stonelog_field_schema_published", {
				log_type_id: logTypeId,
				action: "edit_schema",
			})
			toast.success(`Schema version ${saved?.version ?? ""} saved`)
			setDraft(null)
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to save schema")
		} finally {
			setSaving(false)
		}
	}

	async function handleAddFromLibrary(libraryKeys: string[]) {
		try {
			await addLibraryFields({ logTypeId, libraryKeys, actingOrgId })
			trackEvent("stonelog_field_schema_published", {
				log_type_id: logTypeId,
				action: "add_from_library",
			})
			toast.success(`Added ${libraryKeys.length} field(s) from library`)
			setDraft(null)
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to add fields")
			throw err
		}
	}

	async function handleSeedLibrary() {
		setSeeding(true)
		try {
			await seedLibrary({})
			toast.success("Field library seeded from defaults")
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to seed field library")
		} finally {
			setSeeding(false)
		}
	}

	return (
		<div className="space-y-6">
			{libraryEmpty && (
				<Card>
					<CardContent className="flex flex-col gap-3 sm:flex-row sm:items-start">
						<AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-warning" />
						<div className="flex-1 space-y-2">
							<p className="text-sm font-medium">The global field library is empty</p>
							<p className="text-sm text-foreground-muted">
								Fields reference shared library entries, so saving schema changes — including
								renames — requires a seeded library. Seed it once from the defaults to get started.
							</p>
							{isPlatformAdmin ? (
								<Button size="sm" onClick={handleSeedLibrary} disabled={seeding}>
									{seeding ? "Seeding..." : "Seed field library"}
								</Button>
							) : (
								<p className="text-sm text-foreground-muted">
									Ask a platform admin to seed it from Admin → Field Library.
								</p>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardHeader>
					<CardTitle>Current schema</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
						<div>
							<p className="text-technical mb-1">Version</p>
							<p className="text-sm font-medium">v{current.version}</p>
						</div>
						<div>
							<p className="text-technical mb-1">Source</p>
							<p className="text-sm font-medium">
								{current.source === "default" ? "Default" : `Custom (v${current.version})`}
							</p>
						</div>
						<div>
							<p className="text-technical mb-1">Hash</p>
							<p className="font-mono text-sm font-medium">
								{current.schemaHash?.slice(0, 8) ?? "—"}
							</p>
						</div>
						<div>
							<p className="text-technical mb-1">Fields</p>
							<p className="text-sm font-medium">{fields.length}</p>
						</div>
					</div>
					<div className="mt-4">
						<AlertDialog>
							<AlertDialogTrigger render={<Button variant="destructive" size="sm" />}>
								Reset to defaults
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Reset schema to defaults</AlertDialogTitle>
									<AlertDialogDescription>
										This will create a new schema version with the default fields. The current
										schema will be preserved in version history.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Cancel</AlertDialogCancel>
									<AlertDialogAction
										onClick={async () => {
											try {
												await resetSchema({ logTypeId, actingOrgId })
												trackEvent("stonelog_field_schema_published", {
													log_type_id: logTypeId,
													action: "reset",
												})
												toast.success("Schema reset to defaults")
												setDraft(null)
											} catch (err) {
												Sentry.captureException(err)
												toast.error(err instanceof Error ? err.message : "Failed to reset schema")
											}
										}}
									>
										Reset
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between gap-2">
					<CardTitle>Fields</CardTitle>
					<div className="flex items-center gap-2">
						{dirty && (
							<span
								className="text-xs text-foreground-muted"
								title="Save or discard local edits before adding fields"
							>
								Save changes first
							</span>
						)}
						<Button size="sm" disabled={dirty} onClick={() => setPickerOpen(true)}>
							<PlusIcon />
							Add fields from library
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					<FieldsList
						fields={fields}
						onPatch={patchField}
						onRemove={removeField}
						onMove={moveField}
					/>
					<div className="mt-4 flex items-center justify-end gap-2">
						<Button variant="outline" disabled={!dirty || saving} onClick={() => setDraft(null)}>
							Discard changes
						</Button>
						<Button disabled={!dirty || saving} onClick={handleSave}>
							{saving ? "Saving..." : `Save as v${current.version + 1}`}
						</Button>
					</div>
				</CardContent>
			</Card>

			{versions && versions.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle>Version history</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-3">
							{versions.map((v) => {
								const vFields = v.fields as unknown[]
								const isCurrent = v.version === current.version
								return (
									<div
										key={v._id}
										className="flex items-center justify-between rounded-md border border-border p-3"
									>
										<div className="space-y-1">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium">v{v.version}</span>
												{isCurrent && <Badge variant="default">Current</Badge>}
												{v.rollbackFrom != null && (
													<Badge variant="secondary">
														Rolled back from v{v.rollbackFrom} to v{v.rollbackTo}
													</Badge>
												)}
											</div>
											<div className="flex items-center gap-3 text-xs text-foreground-secondary">
												<span>{new Date(v._creationTime).toLocaleDateString()}</span>
												<span>by {v.updatedBy}</span>
												<span>{vFields.length} fields</span>
												<span className="font-mono">{v.schemaHash.slice(0, 8)}</span>
											</div>
										</div>
										{!isCurrent && (
											<AlertDialog>
												<AlertDialogTrigger render={<Button variant="outline" size="xs" />}>
													Rollback
												</AlertDialogTrigger>
												<AlertDialogContent>
													<AlertDialogHeader>
														<AlertDialogTitle>Rollback to v{v.version}?</AlertDialogTitle>
														<AlertDialogDescription>
															This will create a new version with the fields from v{v.version}.
														</AlertDialogDescription>
													</AlertDialogHeader>
													<AlertDialogFooter>
														<AlertDialogCancel>Cancel</AlertDialogCancel>
														<AlertDialogAction
															onClick={async () => {
																try {
																	await rollbackSchema({
																		logTypeId,
																		targetVersion: v.version,
																		expectedCurrentVersion: currentVersion,
																		actingOrgId,
																	})
																	trackEvent("stonelog_field_schema_published", {
																		log_type_id: logTypeId,
																		action: "rollback",
																		target_version: v.version,
																	})
																	toast.success(`Schema rolled back to v${v.version}`)
																	setDraft(null)
																} catch (err) {
																	Sentry.captureException(err)
																	toast.error(
																		err instanceof Error
																			? err.message
																			: "Failed to rollback schema",
																	)
																}
															}}
														>
															Rollback
														</AlertDialogAction>
													</AlertDialogFooter>
												</AlertDialogContent>
											</AlertDialog>
										)}
									</div>
								)
							})}
						</div>
					</CardContent>
				</Card>
			)}

			<FieldLibraryPicker
				open={pickerOpen}
				onOpenChange={setPickerOpen}
				logTypeSlug={logTypeSlug}
				excludedKeys={usedLibraryKeys}
				onConfirm={handleAddFromLibrary}
			/>
		</div>
	)
}

function FieldsList({
	fields,
	onPatch,
	onRemove,
	onMove,
}: {
	fields: FieldDef[]
	onPatch: (index: number, patch: Partial<FieldDef>) => void
	onRemove: (index: number) => void
	onMove: (from: number, to: number) => void
}) {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)
	const ids = useMemo(() => fields.map((f, i) => `${f.id}-${i}`), [fields])

	function handleDragEnd(event: DragEndEvent) {
		const { active, over } = event
		if (!over || active.id === over.id) return
		const from = ids.indexOf(active.id as string)
		const to = ids.indexOf(over.id as string)
		if (from < 0 || to < 0) return
		onMove(from, to)
	}

	return (
		<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
			<SortableContext items={ids} strategy={verticalListSortingStrategy}>
				<div className="space-y-2">
					{fields.map((field, index) => (
						<SchemaFieldRow
							key={ids[index]}
							sortableId={ids[index]}
							field={field}
							onPatch={(patch) => onPatch(index, patch)}
							onRemove={() => onRemove(index)}
						/>
					))}
				</div>
			</SortableContext>
		</DndContext>
	)
}

function SchemaFieldRow({
	sortableId,
	field,
	onPatch,
	onRemove,
}: {
	sortableId: string
	field: FieldDef
	onPatch: (patch: Partial<FieldDef>) => void
	onRemove: () => void
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: sortableId,
	})
	const isBase = BASE_FIELD_IDS.has(field.id)
	const required = field.validation?.required === true || !!field.required

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Transform.toString(transform),
				transition,
				opacity: isDragging ? 0.5 : 1,
			}}
			className="rounded-md border border-border bg-card"
		>
			<div className="flex items-center gap-2 p-3">
				<button
					type="button"
					className="cursor-grab touch-none text-foreground-muted hover:text-foreground"
					{...attributes}
					{...listeners}
					aria-label="Reorder field"
				>
					<GripVerticalIcon className="size-4" />
				</button>
				<div className="min-w-0 flex-1">
					<Input
						value={field.label}
						onChange={(e) => onPatch({ label: e.target.value })}
						placeholder="Field label"
						aria-label={`Label for field ${field.id}`}
						className="h-8 font-medium"
					/>
					<div className="mt-1 flex flex-wrap items-center gap-2">
						<code className="font-mono text-xs text-foreground-muted">{field.id}</code>
						<Badge variant="secondary">{field.type}</Badge>
						{isBase && <Badge variant="outline">base</Badge>}
					</div>
				</div>
				{!isBase && (
					<Button
						variant="ghost"
						size="icon-xs"
						onClick={onRemove}
						aria-label={`Hide field ${field.id}`}
						title="Hide field (existing data is kept)"
					>
						<TrashIcon />
					</Button>
				)}
			</div>
			<div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border p-3">
				<div className="flex items-center gap-2 text-sm">
					<Switch
						id={`${sortableId}-required`}
						size="sm"
						checked={required}
						onCheckedChange={(checked) =>
							onPatch({ validation: { ...(field.validation ?? {}), required: checked } })
						}
					/>
					<label htmlFor={`${sortableId}-required`}>Required</label>
				</div>
				<div className="flex items-center gap-2 text-sm">
					<label htmlFor={`${sortableId}-group`} className="text-foreground-muted">
						Group
					</label>
					<Input
						id={`${sortableId}-group`}
						value={field.group ?? ""}
						onChange={(e) => onPatch({ group: e.target.value || undefined })}
						placeholder="other"
						className="h-8 w-32"
					/>
				</div>
			</div>
		</div>
	)
}
